import React, { useMemo } from 'react';
import { formatAmount, formatPrice } from '../../utils/formatters';
import { SUPPORTED_CURRENCIES } from '../../utils/currencies';

const formatDateTime = (value) => {
  if (!value) return '—';
  return new Date(value).toLocaleString();
};

// Compact error icon with tooltip
const FieldError = ({ message }) => {
  if (!message) return null;
  return (
    <span
      className="inline-flex ml-1 text-red-500 dark:text-red-400 cursor-help align-middle"
      title={message}
    >
      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
        <path
          fillRule="evenodd"
          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
          clipRule="evenodd"
        />
      </svg>
    </span>
  );
};

const parseOriginalPayload = (payload) => {
  if (!payload) {
    return null;
  }
  try {
    return JSON.parse(payload);
  } catch (_err) {
    return null;
  }
};

const targetFieldLabels = [
  { key: 'DateTime', label: 'Timestamp', validationField: 'timestamp', recordKey: 'timestamp' },
  { key: 'Ticker', label: 'Token', validationField: 'tokenId', recordKey: 'tokenId' },
  { key: 'CoingeckoId', label: 'Token (Coingecko)', validationField: 'tokenId', recordKey: null },
  { key: 'Type', label: 'Transaction Type', validationField: 'transactionType', recordKey: 'transactionType' },
  { key: 'Quantity', label: 'Quantity', validationField: 'quantity', recordKey: 'quantity' },
  { key: 'Price', label: 'Price', validationField: 'price', recordKey: 'price' },
  { key: 'Fees', label: 'Fees', validationField: 'fees', recordKey: 'fees' },
  { key: 'FeeQuantity', label: 'Token Fee', validationField: 'feeQuantity', recordKey: 'feeQuantity' },
  { key: 'Currency', label: 'Currency', validationField: 'currency', recordKey: 'currency' },
  { key: 'Notes', label: 'Notes', validationField: null, recordKey: 'notes' },
  { key: 'Location', label: 'Location', validationField: null, recordKey: 'rawLocation' },
  { key: 'SourceLocation', label: 'Source Location', validationField: null, recordKey: 'rawSourceLocation' },
  { key: 'DestinationLocation', label: 'Destination Location', validationField: null, recordKey: 'rawDestinationLocation' },
];

// Get display value for a record field
const getRecordValue = (record, key) => {
  if (!key) return null;
  switch (key) {
    case 'timestamp':
      return record.timestamp ? new Date(record.timestamp).toLocaleString() : null;
    case 'tokenId':
      return record.token?.ticker || record.tokenId || null;
    case 'transactionType':
      return record.transactionType || null;
    case 'quantity':
      return record.transactionType === 'SPLIT' ? `×${record.quantity}` : record.quantity ? formatAmount(record.quantity) : null;
    case 'price':
      return record.price ? formatPrice(record.price, record.currency) : null;
    case 'fees':
      return record.fees ? formatAmount(record.fees) : null;
    case 'feeQuantity':
      return record.feeQuantity ? formatAmount(record.feeQuantity) : null;
    case 'currency':
      return record.currency || null;
    case 'notes':
      return record.notes || null;
    case 'rawLocation':
      return record.rawLocation || null;
    case 'rawSourceLocation':
      return record.rawSourceLocation || null;
    case 'rawDestinationLocation':
      return record.rawDestinationLocation || null;
    default:
      return null;
  }
};

const previewPrice = (record, isStock) => {
  const currency = (record.currency || '').toUpperCase().trim();
  if (isStock && !/^[A-Z]{3}$/.test(currency)) return formatAmount(record.price);
  return formatPrice(record.price, isStock || SUPPORTED_CURRENCIES.includes(currency) ? currency : undefined);
};

const stockSubLabel = (record) => {
  if (record.token?.ticker) {
    return [record.token.name, record.rawIsin].filter(Boolean).join(' · ');
  }
  return ['No stock found', record.rawExchange].filter(Boolean).join(' · ');
};

const StockInstrumentCell = ({ record, canEdit }) => (
  record.token?.ticker ? (
    <div>
      <div className="font-semibold">{record.token.ticker}</div>
      <div className="text-xs text-gray-500 dark:text-gray-400">{record.token.name || '—'}</div>
      {record.rawIsin && <div className="text-xs font-mono text-gray-400 dark:text-gray-500">{record.rawIsin}</div>}
    </div>
  ) : (
    <div>
      <div className="font-semibold font-mono text-yellow-600 dark:text-yellow-400">{record.rawIsin || '—'}</div>
      <div className="text-xs text-yellow-600 dark:text-yellow-400">
        No stock found{record.rawExchange ? ` on ${record.rawExchange}` : ''}.{canEdit ? ' Use Edit to correct the row or select it and use Set stock.' : ''}
      </div>
    </div>
  )
);

// Broker exports are parsed by a fixed layout, so show the raw row instead of a column mapping.
const OriginalRowList = ({ sourcePairs }) => (
  <dl className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-1 text-xs">
    {sourcePairs.map((pair, idx) => (
      <div key={`${pair.header}-${idx}`} className="flex gap-2 min-w-0">
        <dt className="text-gray-500 dark:text-gray-400 shrink-0">{pair.header || '(currency)'}</dt>
        <dd className="text-gray-900 dark:text-gray-100 break-words">{pair.value || '—'}</dd>
      </div>
    ))}
  </dl>
);

const RecordsTable = ({
  records,
  expandedRowId,
  onToggleRow,
  onToggleSkip,
  pendingRowId,
  fieldMappings,
  onEditRecord,
  selectedIds,
  onSelectionChange,
  readOnly = false,
  assetKind = 'crypto',
}) => {
  const isStock = assetKind === 'stock';
  // All records are selectable for group actions (only when not readOnly)
  const selectableRecords = records;
  const allSelected = !readOnly && selectableRecords.length > 0 && selectableRecords.every((r) => selectedIds.has(r.id));
  const someSelected = !readOnly && selectableRecords.some((r) => selectedIds.has(r.id));

  const handleSelectAll = () => {
    if (readOnly || !onSelectionChange) return;
    if (allSelected) {
      // Deselect all visible
      const next = new Set(selectedIds);
      selectableRecords.forEach((r) => next.delete(r.id));
      onSelectionChange(next);
    } else {
      // Select all visible
      const next = new Set(selectedIds);
      selectableRecords.forEach((r) => next.add(r.id));
      onSelectionChange(next);
    }
  };

  const handleToggleRow = (id) => {
    if (readOnly || !onSelectionChange) return;
    const next = new Set(selectedIds);
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    onSelectionChange(next);
  };
  const mappingDetails = useMemo(() => {
    return targetFieldLabels.map((entry) => {
      const field = fieldMappings?.[entry.key] || fieldMappings?.[entry.key.toUpperCase()];
      const columns = field ? (field.columns || []).map((col) => col.trim()).filter(Boolean) : [];
      return {
        target: entry.label,
        rawTarget: entry.key,
        strategy: field?.strategyKind || '',
        columns,
        validationField: entry.validationField,
        recordKey: entry.recordKey,
        isMapped: columns.length > 0,
      };
    });
  }, [fieldMappings]);

  if (!records.length) {
    return (
      <div className="text-center py-12 text-gray-500 dark:text-gray-400 border border-dashed border-gray-300 dark:border-gray-700 rounded-lg">
        No preview rows have been generated yet.
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
      <div className="divide-y divide-gray-200 dark:divide-gray-700 md:hidden">
        {records.map((record) => {
          const validationErrors = record.validationErrors || [];
          const errorCount = validationErrors.length;
          const hasErrors = !record.isValid;
          const isSkipped = Boolean(record.skipped) && !hasErrors;
          const isExpanded = expandedRowId === record.id;
          const parsedOriginal = parseOriginalPayload(record.originalRow);
          const sourceHeaders = Array.isArray(parsedOriginal?.headers) ? parsedOriginal.headers : [];
          const sourceValues = Array.isArray(parsedOriginal?.values) ? parsedOriginal.values : [];
          const sourcePairs = sourceHeaders.map((header, idx) => ({ header, value: sourceValues[idx] ?? '' }));
          const getValidationMessage = (field) =>
            validationErrors.find((issue) => issue.field?.toLowerCase() === field.toLowerCase())?.message;
          const tokenLabel = isStock
            ? record.token?.ticker || record.rawIsin || '—'
            : record.token?.ticker || record.rawTicker || record.rawCoingeckoId || record.tokenId || '—';
          const tokenSub = isStock
            ? stockSubLabel(record)
            : record.token?.name || record.resolvedTokenName || record.token?.id || '';

          return (
            <div key={record.id} className={`p-3 ${isSkipped ? 'bg-gray-300/60 dark:bg-gray-700/70 opacity-60' : ''}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex min-w-0 items-start gap-3">
                  {!readOnly && (
                    <input
                      type="checkbox"
                      checked={selectedIds.has(record.id)}
                      onChange={() => handleToggleRow(record.id)}
                      disabled={readOnly}
                      className="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 disabled:opacity-60"
                      aria-label="Select preview row"
                    />
                  )}
                  <button
                    type="button"
                    onClick={() => onToggleRow(isExpanded ? null : record.id)}
                    className="min-w-0 text-left"
                    aria-expanded={isExpanded}
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">{tokenLabel}</span>
                      {hasErrors ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-200">
                          {errorCount} {errorCount === 1 ? 'error' : 'errors'}
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-200">
                          Valid
                        </span>
                      )}
                    </div>
                    {tokenSub && <div className="mt-0.5 truncate text-xs text-gray-500 dark:text-gray-400">{tokenSub}</div>}
                    <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      {record.transactionType || '—'} · {record.transactionType === 'SPLIT' ? `×${record.quantity}` : `${formatAmount(record.quantity)} @ ${record.price ? previewPrice(record, isStock) : '—'} · ${record.currency || '—'}`}
                    </div>
                    <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      {record.timestamp ? formatDateTime(record.timestamp) : '—'}
                    </div>
                  </button>
                </div>
                <button
                  type="button"
                  onClick={() => onToggleRow(isExpanded ? null : record.id)}
                  className="shrink-0 text-xs text-blue-600 dark:text-blue-400"
                  aria-expanded={isExpanded}
                >
                  {isExpanded ? 'Hide' : 'Details'}
                </button>
              </div>

              <div className="mt-2 grid grid-cols-2 gap-2 text-xs text-gray-600 dark:text-gray-300">
                <div><span className="text-gray-500 dark:text-gray-400">Fees</span><br />{isStock ? record.fees || '0' : `${record.fees || '0'} / ${record.feeQuantity || '0'}`}</div>
                <div><span className="text-gray-500 dark:text-gray-400">Location</span><br />{record.rawLocation || '—'}</div>
                {!isStock && (
                  <>
                    <div><span className="text-gray-500 dark:text-gray-400">From</span><br />{record.rawSourceLocation || '—'}</div>
                    <div><span className="text-gray-500 dark:text-gray-400">To</span><br />{record.rawDestinationLocation || '—'}</div>
                  </>
                )}
              </div>
              {record.notes && <div className="mt-2 text-xs text-gray-600 dark:text-gray-300">{record.notes}</div>}

              <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
                {!readOnly && onToggleSkip && (
                  <button
                    type="button"
                    disabled={hasErrors || pendingRowId === record.id}
                    onClick={() => onToggleSkip?.(record.id, !record.skipped)}
                    className="inline-flex items-center px-3 py-1.5 font-semibold rounded-md bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {record.skipped ? 'Include' : 'Skip'}
                  </button>
                )}
                {!readOnly && onEditRecord && (
                  <button
                    type="button"
                    onClick={() => onEditRecord(record)}
                    className="inline-flex items-center px-3 py-1.5 font-semibold rounded-md bg-blue-100 text-blue-700 hover:bg-blue-200 dark:bg-blue-900/50 dark:text-blue-300 dark:hover:bg-blue-900/70"
                  >
                    Edit
                  </button>
                )}
              </div>

              {isExpanded && (
                <div className="mt-3 rounded-md border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 p-3">
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">{isStock ? 'Original Row' : 'Original File Mapping'}</h4>
                  {sourcePairs.length === 0 ? (
                    <div className="text-xs text-gray-500 dark:text-gray-400">No source data captured</div>
                  ) : isStock ? (
                    <OriginalRowList sourcePairs={sourcePairs} />
                  ) : (
                    <div className="space-y-2">
                      {renderMappingRows({ record, sourcePairs, mappingDetails, getValidationMessage, variant: 'mobile' })}
                    </div>
                  )}
                  {parsedOriginal?.source && (
                    <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">Source format: {parsedOriginal.source}</p>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
      <div className="hidden overflow-x-auto md:block">
      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
        <thead className="bg-gray-100 dark:bg-gray-700">
          <tr>
            <th className="px-6 py-3 text-center text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider w-10">
              <input
                type="checkbox"
                checked={allSelected}
                ref={(el) => {
                  if (el) el.indeterminate = someSelected && !allSelected;
                }}
                onChange={handleSelectAll}
                disabled={readOnly || selectableRecords.length === 0}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 disabled:opacity-60"
                title={readOnly ? 'Read-only mode' : allSelected ? 'Deselect all' : 'Select all'}
              />
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">Timestamp</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">{isStock ? 'Instrument' : 'Token'}</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">Type</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">Quantity</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">Price</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">Fees</th>
            {!isStock && <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">Token Fee</th>}
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">Currency</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">Notes</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">Location</th>
            {!isStock && <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">From</th>}
            {!isStock && <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">To</th>}
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">Validation</th>
            <th className="px-6 py-3 text-center text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">Skip</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
          {records.map((record) => {
            const validationErrors = record.validationErrors || [];
            const errorCount = validationErrors.length;
            const hasErrors = !record.isValid;
            const isSkipped = Boolean(record.skipped) && !hasErrors;
            const baseRowClasses = 'transition-colors cursor-pointer';
            const rowClasses = (isSkipped
              ? 'bg-gray-300/60 dark:bg-gray-700/70 text-gray-600 dark:text-gray-400'
              : 'hover:bg-gray-50 dark:hover:bg-gray-700')
              .concat(` ${baseRowClasses}`)
              .concat(isSkipped ? ' opacity-50' : '');
            const isExpanded = expandedRowId === record.id;
            const parsedOriginal = parseOriginalPayload(record.originalRow);
            const sourceHeaders = Array.isArray(parsedOriginal?.headers) ? parsedOriginal.headers : [];
            const sourceValues = Array.isArray(parsedOriginal?.values) ? parsedOriginal.values : [];
            const sourcePairs = sourceHeaders.map((header, idx) => ({
              header,
              value: sourceValues[idx] ?? '',
            }));
            const getValidationMessage = (field) =>
              validationErrors.find((issue) => issue.field?.toLowerCase() === field.toLowerCase())?.message;
            return (
              <React.Fragment key={record.id}>
                <tr
                  className={rowClasses}
                  onClick={() => onToggleRow(isExpanded ? null : record.id)}
                  role="button"
                  tabIndex={0}
                  aria-expanded={isExpanded}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      onToggleRow(isExpanded ? null : record.id);
                    }
                  }}
                >
                  <td
                    className="px-6 py-4 text-sm text-center"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <input
                      type="checkbox"
                      checked={selectedIds.has(record.id)}
                      onChange={() => handleToggleRow(record.id)}
                      disabled={readOnly}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 disabled:opacity-60"
                    />
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900 dark:text-white whitespace-nowrap">
                    <span>{record.timestamp ? formatDateTime(record.timestamp) : '—'}</span>
                    <FieldError message={getValidationMessage('timestamp')} />
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-300 break-words max-w-[12rem]">
                    <div className="flex items-start gap-1">
                      {isStock ? (
                        <StockInstrumentCell record={record} canEdit={!readOnly && !!onEditRecord} />
                      ) : record.token?.ticker ? (
                        <div>
                          <div className="font-semibold">{record.token.ticker}</div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            {record.token?.name || record.token?.id || record.tokenId || '—'}
                          </div>
                        </div>
                      ) : record.rawCoingeckoId ? (
                        <div>
                          <div className="font-semibold text-blue-600 dark:text-blue-400">
                            {record.rawTicker || record.rawCoingeckoId}
                          </div>
                          <div className="text-xs text-blue-500 dark:text-blue-400">
                            Will import: {record.resolvedTokenName || record.rawCoingeckoId}
                          </div>
                        </div>
                      ) : record.rawTicker ? (
                        <div>
                          <div className="font-semibold text-yellow-600 dark:text-yellow-400">
                            {record.rawTicker}
                          </div>
                          <div className="text-xs text-yellow-500 dark:text-yellow-400">
                            Ticker not pre-resolved
                          </div>
                        </div>
                      ) : (
                        <span className="text-xs text-gray-500 dark:text-gray-400">{record.tokenId || '—'}</span>
                      )}
                      <FieldError message={getValidationMessage('tokenId')} />
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-300">
                    <span>{record.transactionType}</span>
                    <FieldError message={getValidationMessage('transactionType')} />
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-300">
                    <span>{record.transactionType === 'SPLIT' ? `×${record.quantity}` : formatAmount(record.quantity)}</span>
                    <FieldError message={getValidationMessage('quantity')} />
                  </td>
                  <td className="px-6 py-4 text-sm font-mono text-gray-600 dark:text-gray-300 break-words max-w-[10rem]">
                    <span>
                      {record.price
                        ? previewPrice(record, isStock)
                        : '—'}
                    </span>
                    <FieldError message={getValidationMessage('price')} />
                  </td>
                  <td className="px-6 py-4 text-sm font-mono text-gray-600 dark:text-gray-300 break-words max-w-[10rem]">
                    <span>{record.fees}</span>
                    <FieldError message={getValidationMessage('fees')} />
                  </td>
                  {!isStock && (
                    <td className="px-6 py-4 text-sm font-mono text-gray-600 dark:text-gray-300 break-words max-w-[10rem]">
                      <span>{record.feeQuantity || '0'}</span>
                      <FieldError message={getValidationMessage('feeQuantity')} />
                    </td>
                  )}
                  <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-300">
                    <span>{record.currency}</span>
                    <FieldError message={getValidationMessage('currency')} />
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-300 max-w-[10rem] break-words">
                    {record.notes || '—'}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-300 max-w-[10rem] break-words">
                    {record.rawLocation || '—'}
                  </td>
                  {!isStock && (
                    <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-300 max-w-[10rem] break-words">
                      {record.rawSourceLocation || '—'}
                    </td>
                  )}
                  {!isStock && (
                    <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-300 max-w-[10rem] break-words">
                      {record.rawDestinationLocation || '—'}
                    </td>
                  )}
                  <td className="px-6 py-4 text-sm">
                    {hasErrors ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-200">
                        {errorCount} {errorCount === 1 ? 'error' : 'errors'}
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-200">
                        Valid
                      </span>
                    )}
                  </td>
                  <td
                    className="px-6 py-4 text-sm text-center"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <button
                      type="button"
                      disabled={readOnly || !onToggleSkip || hasErrors || pendingRowId === record.id}
                      onClick={() => onToggleSkip?.(record.id, !record.skipped)}
                      className={`p-1.5 rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-40 disabled:cursor-not-allowed ${
                        hasErrors || record.skipped
                          ? 'text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                          : 'text-gray-500 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20'
                      }`}
                      title={readOnly ? 'Read-only mode' : hasErrors ? 'Cannot skip invalid row' : record.skipped ? 'Include row' : 'Skip row'}
                    >
                      {hasErrors || record.skipped ? (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                        </svg>
                      ) : (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                        </svg>
                      )}
                    </button>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-300">
                    {!readOnly && onEditRecord && (
                      <button
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation();
                          onEditRecord(record);
                        }}
                        className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                      >
                        Edit
                      </button>
                    )}
                  </td>
                </tr>
                {isExpanded && (
                  <tr className="bg-gray-50 dark:bg-gray-800/50 border-t border-gray-200 dark:border-gray-700">
                    <td colSpan={isStock ? 13 : 15} className="px-6 py-4">
                      <div>
                        <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">{isStock ? 'Original Row' : 'Original File Mapping'}</h4>
                        {sourcePairs.length === 0 ? (
                          <div className="px-3 py-2 text-gray-500 dark:text-gray-400 text-center border border-dashed border-gray-300 dark:border-gray-700 rounded-md">
                            No source data captured
                          </div>
                        ) : isStock ? (
                          <OriginalRowList sourcePairs={sourcePairs} />
                        ) : (
                          <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden max-w-full">
                            <div className="overflow-x-auto w-full">
                              <table className="w-full min-w-[800px] divide-y divide-gray-200 dark:divide-gray-700 text-sm">
                                <thead className="bg-gray-100 dark:bg-gray-700">
                                  <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider w-[15%]">
                                      Target Field
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider w-[15%]">
                                      Original Column
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider w-[25%]">
                                      Original Value
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider w-[25%]">
                                      Current Value
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider w-[20%]">
                                      Validation
                                    </th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                                  {renderMappingRows({
                                    record,
                                    sourcePairs,
                                    mappingDetails,
                                    getValidationMessage,
                                  })}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}
                        {parsedOriginal?.source && (
                          <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                            Source format: {parsedOriginal.source}
                          </p>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
      </div>
    </div>
  );
};

export default RecordsTable;

const renderMappingRows = ({
  record,
  sourcePairs,
  mappingDetails,
  getValidationMessage,
  variant = 'table',
}) => {
  // Build index for quick lookup of source values by header
  const sourceIndex = new Map();
  sourcePairs.forEach((pair) => {
    const key = pair.header?.trim().toLowerCase() || '';
    if (key && !sourceIndex.has(key)) {
      sourceIndex.set(key, pair.value || '');
    }
  });

  // Render all target fields
  return mappingDetails.map((detail) => {
    const originalColumns = detail.columns.length > 0 ? detail.columns : null;
    const originalValue = originalColumns
      ? originalColumns.map((col) => {
          const key = col.trim().toLowerCase();
          return sourceIndex.get(key) || '';
        }).filter(Boolean).join(' ') || '—'
      : '—';
    
    const currentValue = getRecordValue(record, detail.recordKey) || '—';
    const validationError = detail.validationField ? getValidationMessage(detail.validationField) : null;

    if (variant === 'mobile') {
      return (
        <div key={`mapping-${detail.rawTarget}`} className="rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-2">
          <div className="flex items-start justify-between gap-2">
            <div className="text-xs font-semibold text-gray-900 dark:text-gray-100">{detail.target}</div>
            {validationError ? (
              <span className="text-xs text-red-600 dark:text-red-400">Issue</span>
            ) : (
              <span className="text-xs text-green-600 dark:text-green-400">✓</span>
            )}
          </div>
          <div className="mt-1 grid grid-cols-1 gap-1 text-xs">
            <div><span className="text-gray-500 dark:text-gray-400">Column:</span> <span className="font-mono text-gray-700 dark:text-gray-300">{originalColumns ? originalColumns.join(', ') : 'Unmapped'}</span></div>
            <div><span className="text-gray-500 dark:text-gray-400">Original:</span> <span className="text-gray-700 dark:text-gray-300 break-words">{originalValue}</span></div>
            <div><span className="text-gray-500 dark:text-gray-400">Current:</span> <span className="font-medium text-gray-900 dark:text-white break-words">{currentValue}</span></div>
            {validationError && <div className="text-red-600 dark:text-red-400 break-words">{validationError}</div>}
          </div>
        </div>
      );
    }

    return (
      <tr key={`mapping-${detail.rawTarget}`} className="hover:bg-gray-50 dark:hover:bg-gray-700">
        <td className="px-6 py-4 text-gray-900 dark:text-white align-top font-semibold text-sm">
          {detail.target}
        </td>
        <td className="px-6 py-4 text-gray-600 dark:text-gray-300 font-mono align-top break-words text-xs">
          {originalColumns ? originalColumns.join(', ') : (
            <span className="text-gray-400 dark:text-gray-500 italic">Unmapped</span>
          )}
        </td>
        <td className="px-6 py-4 text-gray-600 dark:text-gray-300 align-top break-words text-sm">
          {originalValue}
        </td>
        <td className="px-6 py-4 text-gray-900 dark:text-white align-top break-words font-medium text-sm">
          {currentValue}
        </td>
        <td className="px-6 py-4 align-top text-sm">
          {validationError ? (
            <span className="text-red-600 dark:text-red-400 text-xs">
              {validationError}
            </span>
          ) : (
            <span className="text-green-600 dark:text-green-400 text-xs">✓</span>
          )}
        </td>
      </tr>
    );
  });
};


